import React, { useState, useEffect } from "react";
import "./userManagement.css";
import { getAllUsers, toggleUserEnabled } from "../../services/userManagementService";


interface User {
  id: string;
  userName: string;
  role: string;
  enabled: boolean;
}

interface UserData {
  id: string;
  userName: string;
  role: string;
}

interface UserManagementProps {
  isOpen: boolean;
  onClose: () => void;
}

const UserManagement: React.FC<UserManagementProps> = ({
  isOpen,
  onClose
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [togglingUserId, setTogglingUserId] = useState<string | null>(null);
  const [error, setError] = useState<string|null>(null);

  useEffect(() => {
    if(isOpen){
      fetchUsers();
    }
  }, [isOpen])

  const fetchUsers = async () => {
     setLoading(true);
     setError(null);
     try {
        const response = await getAllUsers();
        setUsers(response.data);
     } catch (err: any) {
        console.error("Error fetching users:", err);
        setError(err?.response?.data?.message || "Failed to load users");
     } finally {
        setLoading(false);
     }
  };
  
  const handleToggleActive = async (userId: string) => {
    const user = users.find((u) => u.id === userId);
    if (!user) return;

    const newEnabledState = !user.enabled;

    // Optimistic update
    setUsers(
      users.map((u) =>
        u.id === userId ? { ...u, enabled: newEnabledState } : u
      )
    );

    // Set loading state for this specific user
    setTogglingUserId(userId);

    try {
      await toggleUserEnabled(userId, newEnabledState);
      // Success - the optimistic update is already applied
    } catch (err: any) {
      console.error("Error toggling user status:", err);
      
      // Revert on error
      setUsers(
        users.map((u) =>
          u.id === userId ? { ...u, enabled: user.enabled } : u
        )
      );
      
      // Show error message
      setError(
        err?.response?.data?.message || 
        `Failed to ${newEnabledState ? 'enable' : 'disable'} user`
      );
    } finally {
      setTogglingUserId(null);
    }
  };

  const handleDeleteUser = (userId: number) => {
    const user = users.find((u) => u.id === userId);
    if (user) {
      onDeleteUser({
        id: user.id,
        userId: user.userId,
        role: user.role,
      });
    }
  };


  if (!isOpen) return null;

  return (
    <div className="user-management-overlay" onClick={onClose}>
      <div className="user-management-wrapper">
        <button className="user-management-close" onClick={onClose}>
          ×
        </button>
        <div
          className="user-management-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="user-management-content">
            <h2 className="user-management-title">User Management</h2>
            <p className="user-management-description">
                List, Enable and Disable Users
            </p>

            <div className="user-management-table-container">
              <table className="user-management-table">
                <thead>
                  <tr className="user-management-header-row">
                    <th className="user-management-header">Email</th>
                    <th className="user-management-header">Role</th>
                    <th className="user-management-header">Active User</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="user-management-row">
                      <td className="user-management-cell">{user.userName}</td>
                      <td className="user-management-cell">{user.role}</td>
                      <td className="user-management-cell">
                        <div
                          className={`user-management-toggle ${
                            user.enabled ? "active" : ""
                          } ${togglingUserId === user.id ? "toggling" : ""}`}
                          onClick={() => handleToggleActive(user.id)}
                          style={{
                            opacity: togglingUserId === user.id ? 0.5 : 1,
                            cursor: togglingUserId === user.id ? "wait" : "pointer"
                          }}
                        >
                          <div className="user-management-toggle-circle"></div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
